import clsx from "clsx";

import { useMoneySaved } from "./MoneySavedContext";
import { SlotMachineCurrency } from "./SlotMachineCurrency";
import { useScrollReveal } from "./useScrollReveal";
import { formatCurrency } from "./utils";

function RevealElement({
  children,
  isVisible,
  delay,
  className,
}: {
  children: React.ReactNode;
  isVisible: boolean;
  delay: number;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        "transition-all duration-1000",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0",
        className,
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export function EstimatedSavingsBand() {
  const { totalSavings, totalSpend, cacReductionPct, totalExtraConversions } =
    useMoneySaved();
  const { ref, isVisible } = useScrollReveal();

  if (totalSavings <= 0) return null;

  return (
    <section
      ref={ref}
      className="scroll-section relative bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900"
    >
      <div className="pointer-events-none absolute top-1/3 left-1/4 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-48 w-48 rounded-full bg-purple-500/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-[960px] px-4 text-center">
        <RevealElement isVisible={isVisible} delay={0}>
          <p className="text-xs font-semibold tracking-[0.2em] text-indigo-400 uppercase">
            The Bigger Picture
          </p>
        </RevealElement>

        <RevealElement isVisible={isVisible} delay={200}>
          <h2 className="mx-auto mt-6 max-w-lg text-3xl leading-tight font-extrabold text-white md:text-5xl">
            Every dollar working harder.
          </h2>
        </RevealElement>

        <RevealElement isVisible={isVisible} delay={400}>
          <p
            className="mt-8 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text py-2 leading-none font-extrabold tracking-tight text-transparent"
            style={{ fontSize: "clamp(2rem, 8vw, 4.5rem)" }}
          >
            <SlotMachineCurrency
              value={totalSavings}
              trigger={isVisible}
              formatFn={formatCurrency}
            />
          </p>
          <p className="mt-2 text-sm font-semibold tracking-wide text-indigo-400/70 uppercase">
            Total Saved
          </p>
        </RevealElement>

        <RevealElement isVisible={isVisible} delay={600}>
          <p className="mx-auto mt-8 max-w-lg text-lg leading-relaxed text-slate-400">
            Of the{" "}
            <strong className="text-slate-300">
              {formatCurrency(totalSpend)}
            </strong>{" "}
            you spent on ads, Freshpaint saved you{" "}
            <strong className="text-slate-300">
              {formatCurrency(totalSavings)}
            </strong>
            {cacReductionPct != null && cacReductionPct > 0 && (
              <>
                {" "}
                — reducing your CAC by{" "}
                <strong className="text-slate-300">
                  {cacReductionPct.toFixed(1)}%
                </strong>
              </>
            )}
            {totalExtraConversions > 0 && (
              <>
                {" "}
                and drove{" "}
                <strong className="text-slate-300">
                  {Math.round(totalExtraConversions).toLocaleString()}
                </strong>{" "}
                extra conversions
              </>
            )}
            .
          </p>
        </RevealElement>
      </div>
    </section>
  );
}
